"use client";

import { signIn } from "next-auth/react";
import { useLocale } from "next-intl";
import { useState, useEffect } from "react";
import { Link } from "@/i18n/routing";
import { FaEye, FaEyeSlash, FaCircleNotch, FaCopy } from "react-icons/fa";
import MailChecker from "mailchecker";
import validator from "validator";
import { useTranslations } from "next-intl";
import { FcGoogle } from "react-icons/fc";
import { saveCheckoutMeta } from "../checkout";
import { useSession } from "next-auth/react";
import { IoMdClose } from "react-icons/io";
import useFetcherUser from "../checkout/FtecherUser";

interface AuthPopupProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: "login" | "signup";
}

interface SessionUser {
  user?: {
    id?: string;
    email?: string | null;
    full_name?: string | null;
  };
}

const IS_DEV = process.env.NODE_ENV === "development";
const MAIN_ORIGIN = IS_DEV
  ? "http://localhost:3000"
  : "https://padelmasterthegame.com";

const localeOfferPaths: Record<string, string> = {
  en: "/account/offer", fr: "/compte/offer", ar: "/الحساب/العروض",
  nl: "/account/offers", de: "/konto/angebote", pt: "/conta/ofertas",
  sv: "/konto/erbjudanden", ru: "/аккаунт/предложения",
  it: "/account/offerte", es: "/cuenta/ofertas",
};

const localeSubdomains: Record<string, string> = {
  en: "padelmasterthegame.com", fr: "fr.padelmasterthegame.com",
  ar: "ar.padelmasterthegame.com", it: "it.padelmasterthegame.com",
  ru: "ru.padelmasterthegame.com", de: "de.padelmasterthegame.com",
  sv: "sv.padelmasterthegame.com", nl: "nl.padelmasterthegame.com",
  es: "es.padelmasterthegame.com", pt: "pt.padelmasterthegame.com",
};

// ─── Locale → /account path ──────────────────────────────────────────────────
// The dashboard (DAshboarduser) already handles ?purchaseAfter=1.
// After Google OAuth we MUST land on /account — not the current popup page.
const localeAccountPaths: Record<string, string> = {
  en: "/account", fr: "/compte", ar: "/الحساب",
  nl: "/account", de: "/konto", pt: "/conta",
  sv: "/konto", ru: "/аккаунт", it: "/account", es: "/cuenta",
};

export default function RedirectToCard({
  isOpen,
  onClose,
  initialMode = "login",
}: AuthPopupProps) {
  const t = useTranslations("Login");
  const locale = useLocale();
  const th = useTranslations("Padel-Master-The-Game-Home-login-signin");
  const [affiliateCode, setAffiliateCode] = useState<string | null>(null);
  const finalPriceId = process.env.NEXT_PUBLIC_PADDLE_PRICE_ID || "";
  const { data: session } = useSession();
  const { userinfo } = useFetcherUser();

  useEffect(() => {
    const code = localStorage.getItem("affiliate");
    setAffiliateCode(code);
  }, []);

  // ─── Core purchase handler ────────────────────────────────────────────────
  const handlePurchase = async (sessionOverride?: SessionUser | null) => {
    const activeSession = sessionOverride ?? session;

    if (!window.Paddle) {
      console.warn("Paddle is not loaded yet.");
      return;
    }
    if (!finalPriceId) {
      console.warn("No Paddle price ID configured.");
      return;
    }

    const userEmail    = activeSession?.user?.email ?? null;
    const userFullName = (activeSession?.user as any)?.full_name ?? null;
    const userId       = activeSession?.user?.id ?? null;

    const domain     = localeSubdomains[locale] ?? "padelmasterthegame.com";
    const path       = localeOfferPaths[locale] ?? "/account/offer";
    const successUrl = `https://${domain}${path}`;

    window.Paddle.Checkout.open({
      settings: { successUrl },
      items: [{ priceId: finalPriceId, quantity: 1 }],
      customer: { email: userEmail },
      customData: {
        userId,
        affiliate_code: affiliateCode,
        email_user:     userEmail,
        name_user:      userFullName,
        language:       locale,
      },
    });

    if (userId) {
      await saveCheckoutMeta({
        userId,
        email:         userEmail,
        name:          userFullName,
        language:      locale,
        affiliateCode,
      });
    }
  };

  // ─── Form / UI state ──────────────────────────────────────────────────────
  const [mode, setMode] = useState<"login" | "signup">(initialMode);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [error1, setError1] = useState("");
  const [error2, setError2] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordSuggestion, setPasswordSuggestion] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [email, setEmail] = useState("");
  const [emailsend, setEmailsend] = useState(false);
  const [emailsenderror, setEmailsenderror] = useState(false);
  const [enteremail, setEnteremail] = useState(false);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen]);

  useEffect(() => {
    setError(null); setError1(""); setError2("");
    setEmailsend(false); setEmailsenderror(false); setEnteremail(false);
    setPasswordSuggestion(""); setShowPassword(false); setIsCopied(false);
  }, [mode]);

  const isValidEmail = async (
    emailValue: string
  ): Promise<{ valid: boolean; message?: string }> => {
    if (!emailValue?.trim())
      return { valid: false, message: t("Email-is-required") };
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue))
      return { valid: false, message: t("Invalid-email-format") };
    if (!validator.isEmail(emailValue))
      return { valid: false, message: t("Invalid-email-format") };
    if (!MailChecker.isValid(emailValue))
      return { valid: false, message: t("Disposable-emails") };
    return { valid: true };
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) return t("8-characters");
    if (!/[A-Z]/.test(password)) return t("uppercase");
    if (!/[a-z]/.test(password)) return t("lowercase");
    if (!/[0-9]/.test(password)) return t("number");
    if (!/[!@#$%^&*]/.test(password)) return t("character");
    return null;
  };

  const resetPassword = async () => {
    if (!email) { setEnteremail(true); return; }
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_URL}auth/users/reset_password/`,
        { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) }
      );
      if (!res.ok) throw new Error();
      setEmailsend(true);
    } catch { setEmailsenderror(true); }
  };

  // ─── Google login ─────────────────────────────────────────────────────────
  // KEY FIX: query params (?purchaseAfter=1) get silently dropped by next-intl's
  // domain + pathnames-based middleware when it internally rewrites/redirects
  // based on the localized pathname map. sessionStorage survives the OAuth
  // round-trip regardless of what middleware does to the URL, since it's
  // never part of the request at all.
  const handleGoogleLogin = () => {
    setIsGoogleLoading(true);
    setError(null);
    sessionStorage.setItem("postAuthIntent", "purchase");
    const domain      = localeSubdomains[locale] ?? "padelmasterthegame.com";
    const accountPath = localeAccountPaths[locale] ?? "/account";
    const callbackUrl = `https://${domain}${accountPath}`;
    signIn("google", { callbackUrl });
  };

  const generatePasswordSuggestion = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
    let pw = "";
    for (let i = 0; i < 12; i++)
      pw += chars.charAt(Math.floor(Math.random() * chars.length));
    setPasswordSuggestion(pw);
  };

  const handleCopyPassword = () => {
    if (!passwordSuggestion) return;
    navigator.clipboard.writeText(passwordSuggestion).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  // ─── Post-credentials helper ──────────────────────────────────────────────
  // forcePurchase=true bypasses the stale userinfo check — after a fresh
  // credentials login we don't wait for useFetcherUser to reload.
  const closeAndMaybePurchase = async (
    freshSession: SessionUser | null,
    forcePurchase = false
  ) => {
    onClose();
    if (forcePurchase || userinfo?.is_payed === false) {
      await handlePurchase(freshSession);
    }
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null); setError1(""); setError2("");
    const formData = new FormData(e.currentTarget);
    const emailVal = formData.get("email") as string;
    const password = formData.get("password") as string;
    const emailValidation = await isValidEmail(emailVal);
    if (!emailValidation.valid) { setError1(emailValidation.message || t("Invalid-email")); setIsLoading(false); return; }
    const passwordError = validatePassword(password);
    if (passwordError) { setError2(passwordError); setIsLoading(false); return; }
    try {
      await fetch(`${process.env.NEXT_PUBLIC_URL}auth/users/`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailVal, password }),
      });
      const result = await signIn("credentials", { redirect: false, email: emailVal, password });
      if (result?.error) {
        setError(t("Please-verify-your-email-or-password"));
      } else {
        const { getSession } = await import("next-auth/react");
        const freshSession = await getSession();
        await closeAndMaybePurchase(freshSession, true);
      }
    } catch {
      setError(t("An-error-occurred-during-sign-in"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null); setError1(""); setError2("");
    const formData = new FormData(e.currentTarget);
    const emailVal = formData.get("email") as string;
    const password = formData.get("password") as string;
    const emailValidation = await isValidEmail(emailVal);
    if (!emailValidation.valid) { setError1(emailValidation.message || t("Invalid-email")); setIsLoading(false); return; }
    try {
      const result = await signIn("credentials", { redirect: false, email: emailVal, password });
      if (result?.error) {
        setError(t("Please-verify-your-email-or-password"));
      } else {
        const { getSession } = await import("next-auth/react");
        const freshSession = await getSession();
        await closeAndMaybePurchase(freshSession, true);
      }
    } catch {
      setError(t("An-error-occurred-during-sign-in"));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-70 z-50 transition-opacity duration-300"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto">
          <div className="relative rounded-2xl shadow-xl overflow-hidden border border-gray-600 bg-black bg-opacity-90 backdrop-filter backdrop-blur-sm animate-fade-in-up">
            <button
              onClick={onClose}
              className="absolute top-2 right-2 flex justify-end items-end text-white hover:text-gray-300 transition-colors z-10"
            >
              <IoMdClose className="w-6 h-6" />
            </button>
            <div className="p-6 sm:p-8">
              <form
                onSubmit={mode === "login" ? handleLogin : handleSignUp}
                className="space-y-4"
              >
                <h1 className="font-extrabold text-2xl text-b uppercase">{th("title")}</h1>
                <div className="mb-6">
                  <h3 className="text-b text-sm font-semibold uppercase">
                    {mode === "login" ? th("Login") : th("Signup")}
                  </h3>
                  <h2 className="text-gray-100 text-sm mt-1">
                    {mode === "login" ? th("login-text") : th("Signup-text")}
                  </h2>
                </div>
                <input
                  type="email" name="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("email")}
                  className="w-full px-4 py-2 border text-white bg-slate-800 placeholder:text-gray-300 border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-b"
                />
                {error1 && <p className="text-b text-sm">{error1}</p>}
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"} name="password"
                    placeholder={t("password")}
                    className="w-full px-4 py-2 border text-white bg-slate-800 placeholder:text-gray-300 border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-b"
                    onFocus={() => { if (mode === "signup") generatePasswordSuggestion(); }}
                  />
                  <button
                    type="button" onClick={() => setShowPassword((v) => !v)}
                    className={`absolute top-2 text-gray-500 hover:text-gray-700 flex items-center justify-center w-6 h-6 ${locale === "ar" ? "left-2" : "right-2"}`}
                  >
                    {showPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                  </button>
                </div>
                {mode === "signup" && passwordSuggestion && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-gray-300 text-sm">
                      {t("text-5")} {th("suggestion")}<br />
                      <span className="font-semibold text-b">
                        {passwordSuggestion}
                        <button type="button" onClick={handleCopyPassword} className="text-gray-300 hover:text-gray-700 ml-2">
                          <FaCopy className="w-3 h-3 inline" />
                        </button>
                      </span>
                    </p>
                    {isCopied && <span className="text-sm text-green-500">{t("Copied")}</span>}
                  </div>
                )}
                {error2 && <p className="text-b text-sm">{error2}</p>}
                {error && <p className="text-b text-sm">{error}</p>}
                {mode === "login" && (
                  <>
                    <p className="text-white text-sm">
                      {th("forgot")}{" "}
                      <span className="font-semibold underline cursor-pointer" onClick={resetPassword}>
                        {t("text-7")}
                      </span>
                    </p>
                    {emailsend && <p className="text-b text-sm">{t("emailsend")}</p>}
                    {emailsenderror && <p className="text-b text-sm">{t("emailsenderror")}</p>}
                    {enteremail && <p className="text-b text-sm">{t("enteremail")}</p>}
                  </>
                )}
                <div className="flex flex-col gap-2">
                  <button
                    type="submit" disabled={isLoading}
                    className="w-full bg-b text-a py-3 rounded-lg text-sm hover:bg-a hover:text-white font-semibold transition-colors capitalize flex gap-3 justify-center items-center disabled:opacity-70"
                  >
                    {isLoading ? (
                      <>{mode === "login" ? th("Login") : th("Start-now")}… <FaCircleNotch className="animate-spin w-5 h-5" /></>
                    ) : (
                      <>{mode === "login" ? th("Login") : th("Start-now")}</>
                    )}
                  </button>
                  <div className="flex items-center gap-3 my-2">
                    <div className="flex-1 border-t border-gray-500" />
                    <p className="text-gray-400 text-sm font-medium">{th("Or")}</p>
                    <div className="flex-1 border-t border-gray-500" />
                  </div>
                  <button
                    type="button" onClick={handleGoogleLogin} disabled={isGoogleLoading}
                    className="border border-white w-full rounded-lg py-3 px-12 bg-black hover:bg-gray-800 cursor-pointer flex gap-2 items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isGoogleLoading ? (
                      <FaCircleNotch className="animate-spin w-5 h-5 text-white" />
                    ) : (
                      <><FcGoogle size={24} /><p className="text-white capitalize font-semibold text-sm">{th("google")}</p></>
                    )}
                  </button>
                </div>
                <p className="text-gray-100 text-xs text-center mt-4 capitalize">
                  {mode === "login" ? (
                    <>{th("new")}{" "}<span onClick={() => setMode("signup")} className="text-b font-semibold underline cursor-pointer">{th("create")}</span></>
                  ) : (
                    <>{th("have-account")}{" "}<span onClick={() => setMode("login")} className="text-b font-semibold underline cursor-pointer">{th("Login-here")}</span></>
                  )}
                </p>
                {mode === "signup" && (
                  <p className="text-gray-400 text-xs text-center mt-2">
                    {th("registering")}{" "}
                    <Link href="/privacy-policy" className="font-semibold underline">{th("Privacy-Policy")}</Link>
                    {" "}{th("and")}{" "}
                    <Link href="/terms-and-conditions" className="font-semibold underline">{th("Terms-Conditions")}</Link>
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in-up { animation: fadeInUp 0.3s ease-out; }
      `}</style>
    </>
  );
}